import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AppButton, AppShell, CenteredCard } from '@/pages/external/components/shell'
import { Avatar } from '@/pages/external/components'
import { buildOAuthLaunchUrl, mintOAuthLoginToken } from '@/services/oauth'
import { OAuthError, OAuthLoading } from './OAuthStates'
import { useConnectSession } from './useConnectSession'

/** Only http(s) targets are allowed for the partner-first bounce (no javascript:/data: URIs). */
const isSafeExternalUrl = (url: string | null): url is string => {
  if (!url) return false
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch {
    return false
  }
}

/**
 * Promo resolver. Route: `/connect?client_id=<partnerClientId>&login_url=<partnerLoginUrl>[&promo=]`.
 *
 * The landing target for DUPR promo links clicked OUTSIDE the app/web. It decides who logs in first:
 *
 *   existing DUPR session → mandatory one-tap "Continue as {user}?" → POST /oauth/v1.0/login-token
 *       (mint an OTC) → top-level redirect to `/oauth/v1.0/launch?otc=…&client_id=…` (DUPR-first).
 *   no session          → top-level redirect to the partner's login URL (partner-first) so the
 *       partner logs in their user, then bounces back into `/oauth/v1.0/authorize`.
 *
 * The OTC is minted only from an authenticated session after an explicit tap — there is no silent
 * ambient-cookie trust, which keeps this safe on shared machines.
 */
export function ConnectResolverPage() {
  const [searchParams] = useSearchParams()
  const clientId = searchParams.get('client_id')
  const partnerLoginUrl = searchParams.get('login_url') ?? searchParams.get('oauth_login_url')
  const promo = searchParams.get('promo') ?? undefined

  const { hasSession, user, isInitializing } = useConnectSession()

  const [minting, setMinting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [redirecting, setRedirecting] = useState(false)

  const goToPartner = useCallback(() => {
    if (isSafeExternalUrl(partnerLoginUrl)) {
      setRedirecting(true)
      window.location.assign(partnerLoginUrl)
    } else {
      setError('This connection link is misconfigured. Please contact the partner you started from.')
    }
  }, [partnerLoginUrl])

  // No session → partner-first: bounce to the partner's own login.
  useEffect(() => {
    if (!clientId || isInitializing || hasSession) return
    goToPartner()
  }, [clientId, isInitializing, hasSession, goToPartner])

  const handleContinue = async () => {
    if (!hasSession || !clientId) return
    setMinting(true)
    setError(null)
    try {
      const { token } = await mintOAuthLoginToken()
      // Top-level navigation: the backend consumes the OTC, sets the __Host cookie, and bounces
      // through the partner login before returning to /oauth/authorize.
      window.location.assign(buildOAuthLaunchUrl(token, clientId, promo ? { promo } : undefined))
    } catch (e: unknown) {
      setMinting(false)
      setError(e instanceof Error ? e.message : 'We could not start the connection. Please try again.')
    }
  }

  if (!clientId) {
    return (
      <OAuthError
        title="Connection unavailable"
        message="This link is missing the partner it should connect to. Please start again from the partner."
      />
    )
  }

  if (isInitializing) {
    return <OAuthLoading message="Checking your DUPR account…" />
  }

  // No session — redirecting to partner (or an error was surfaced above).
  if (!hasSession || !user) {
    return error ? (
      <OAuthError title="Connection unavailable" message={error} />
    ) : (
      <OAuthLoading message="Redirecting you to sign in…" />
    )
  }

  if (redirecting) {
    return <OAuthLoading message="Redirecting…" />
  }

  // Existing session → mandatory one-tap identity confirmation before minting the OTC.
  return (
    <AppShell logoLinksToDashboard={false}>
      <CenteredCard>
        <div className="flex flex-col items-center gap-y-4 w-full">
          <p className="text-xs uppercase tracking-widest text-[color:var(--ink-faint)]">Connect with DUPR</p>
          <Avatar src={user.imageUrl} alt={user.fullName} />
          <div className="text-center">
            <p className="text-[color:var(--ink-soft)] text-sm">Continue as</p>
            <p className="text-[color:var(--ink)] font-semibold text-lg">{user.fullName}</p>
            <p className="text-[color:var(--ink-soft)] text-sm">DUPR {user.referralCode}</p>
          </div>

          {error && (
            <p
              className="text-sm text-[color:var(--negative)] bg-[color:var(--negative)]/8 border border-[color:var(--negative)]/20 rounded-[var(--radius-input)] px-3 py-2 w-full text-center"
              role="alert"
            >
              {error}
            </p>
          )}

          <div className="flex flex-col w-full gap-y-2 mt-2">
            <AppButton fullWidth onClick={handleContinue} loading={minting} disabled={minting}>
              {minting ? 'Starting…' : `Continue as ${user.fullName?.split(' ')[0] || 'me'}`}
            </AppButton>
            <AppButton variant="ghost" fullWidth onClick={goToPartner} disabled={minting}>
              Use a different account
            </AppButton>
          </div>
        </div>
      </CenteredCard>
    </AppShell>
  )
}

export default ConnectResolverPage
